import * as React from 'react';
import { connect } from 'react-redux';
import { capitalize, isNil, sortBy } from 'lodash';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import StatusLabel from 'components/status-label/StatusLabel';
import MediaPreview from 'components/MediaPreview';
import GalleryModalAsset from 'components/gallery-modal/gallery-modal-asset/GalleryModalAsset';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withModal } from 'components/modal/ModalHOC';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import RealmOutfitReview from '../realm-outfit-review/RealmOutfitReview';
import RealmOutfitsImages from '../realm-outfits-images/RealmOutfitsImages';
import RealmOutfitVideo from '../realm-outfit-video/RealmOutfitVideo';
import OutfitDeclineHistory from '../outfit-decline-history/OutfitDeclineHistory';

import {
  downloadFileIndividually,
  downloadImages,
  unPublishRealmOutfit
} from 'modules/realm-outfits/reducers/realm-outfits';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import {
  REALM_GENDER_OPTIONS,
  REALM_OUTFIT_STATUS_APPROVED,
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_PUBLISHED,
  splitTagsByType
} from 'utils/realm-outfits';
import { getDisplayDate, getDisplayDateRange } from 'utils/date';
import { RealmOutfitAudioType } from 'modules/realm-outfits/types/RealmOutfit.type';

import './realm-outfit-details.scss';

applyTranslations(require('./realm-outfit-details.i18n.json'));

import type {
  Campaign,
  GalleryModalItem,
  RealmOutfit,
  RealmOutfitItem,
  RealmOutfitTag,
  RealmOutfitVideo as Video
} from 'types';

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

type OwnProps = {
  outfit: RealmOutfit,
  deliverableId?: number, // eslint-disable-line react/no-unused-prop-types
  hasProcessingCompleted?: boolean,
  onRetriggerVideoProcessing?: () => void,
  isBrand?: boolean
};

type DispatchProps = {
  dispatchDownloadImages: (outfitId: number) => void,
  dispatchUnpublishOutfit: (outfitId: number) => void,
  dispatchShowConfirmModal: (props: {
    message: string,
    onConfirm: () => void,
    onCancel: () => void
  }) => Promise<void>,
  dispatchHideConfirmModal: () => Promise<void>,
  dispatchDownloadFileIndividually: (url: string) => void
};

type StateProps = {
  campaign: Campaign,
  isUnpublishing: boolean,
  realmOutfitAudioTypeEnabled: boolean
}

type Props = OwnProps & DispatchProps & StateProps;

class RealmOutfitDetails extends React.Component<Props> {
  private handleDownloadAllOutfitImages = () => {
    const { outfit, dispatchDownloadImages } = this.props;

    dispatchDownloadImages(outfit.id);
  };

  private handleUnpublish = () => {
    const { outfit, dispatchUnpublishOutfit, dispatchShowConfirmModal, dispatchHideConfirmModal } = this.props;

    dispatchShowConfirmModal({
      message: __t('realm_outfit_details.confirmation_unpublish.message'),
      onConfirm: () => {
        dispatchUnpublishOutfit(outfit.id);

        dispatchHideConfirmModal();
      },
      onCancel: () => dispatchHideConfirmModal()
    });
  };

  private renderSection(heading: React.ReactNode, content: React.ReactNode) {
    return (
      <div className="realm-outfit-details__section">
        <div className="realm-outfit-details__heading">
          {heading}
        </div>
        {content}
      </div>
    );
  }

  private renderTags(tags: Array<string> = []) {
    if (!tags || tags.length === 0) {
      return '—';
    }

    return (
      <React.Fragment>
        {
          tags.map(tag => (
            <span
              key={tag}
              className="realm-outfit-details__tag"
            >
              <StatusLabel
                type="neutral"
                size="small"
                label={tag}
              />
            </span>
          ))
        }
      </React.Fragment>
    );
  }

  private renderRealmTags(tags: Array<RealmOutfitTag>) {
    if (!tags || tags.length === 0) {
      return '—';
    }

    const selectedTags = tags.map(tag => capitalize(tag.title));
    return this.renderTags(selectedTags);
  }

  private renderAudioType(audioType: RealmOutfitAudioType | null) {
    return this.renderTags(audioType ? [__t(`realm_outfit_details.audio_types.${audioType}`)] : []);
  }

  private renderRealmItemDetails(outfitItem: RealmOutfitItem) {
    return (
      <div
        className="realm-outfit-details__item-label-container"
        onClick={event => {
          event.stopPropagation();

          window.open(outfitItem.itemUrl, '_blank');
        }}
      >
        <div className="text-overflow-ellipsis realm-outfit-details__item-label-btn">
          {__t(`realm_outfit_details.item.${outfitItem.isSimilar ? 'similar' : 'exact'}`)}
        </div>
      </div>
    );
  }

  private renderCustomAssetForItem = (item: GalleryModalItem, index: number) => {
    const { outfit } = this.props;

    return (
      <div className="realm-outfit-details__gallery-asset">
        <GalleryModalAsset
          selectedItem={item}
          currentPosition={index}
        />
        <div className="realm-outfit-details__gallery-asset__actions">
          {this.renderRealmItemDetails(outfit.items[index])}
        </div>
      </div>
    );
  };

  private renderItems(items: Array<RealmOutfitItem> = []) {
    if (items.length === 0) {
      return '—';
    }

    const attachments = items.map(item => ({
      id: item.id,
      url: item.imageUrl,
      assetType: 'image',
      meta: {
        type: 'highres'
      }
    }));

    const galleryItems = prepareDeliverableAttachmentsForGallery(attachments);

    return (
      <div className="realm-outfit-details__items">
        {
          attachments.map((attachment, index) => (
            <DivWithModal
              key={`attachment-${attachment.id}`}
              modalContentProps={{
                renderCustomAsset: this.renderCustomAssetForItem,
                items: galleryItems,
                currentPosition: index
              }}
            >
              <div title={`SKU-${items[index].sku}`}>
                <MediaPreview
                  className="realm-outfit-details__preview"
                  attachment={{ ...attachment, format: attachment.assetType }}
                  width={120}
                  height={120}
                />
                {this.renderRealmItemDetails(items[index])}
              </div>
            </DivWithModal>
          ))
        }
      </div>
    );
  }

  private renderSelect(name: 'genders', items: Array<string>) {
    const selectedItems = REALM_GENDER_OPTIONS.filter(item => items.includes(item));

    if (selectedItems.length === 0) {
      return '—';
    }

    const selectedTags = selectedItems.map(item =>
      __t(`realm_outfit_details.${name}.${item}`)
    );
    return this.renderTags(selectedTags);
  }

  private renderReviewDate(reviewedAt: string): React.ReactNode {
    return (
      <div
        className="realm-outfit-details__dates"
        title={reviewedAt}
      >
        {getDisplayDate(reviewedAt) || '—'}
      </div>
    );
  }

  private renderDatesInput(startDate, endDate): React.ReactNode {
    if (startDate === null && endDate === null) {
      return '—';
    }

    const deliverableDates = getDisplayDateRange(startDate, endDate);

    return (
      <div
        className="realm-outfit-details__dates"
        title={deliverableDates}
      >
        {deliverableDates}
      </div>
    );
  }

  private renderPublishedError(): React.ReactNode {
    return (
      <div className="realm-outfit-details__error">
        {__t('realm_outfit_details.errors.publishing')}
      </div>
    );
  }

  private renderContextMenu = () => {
    if (!this.props.outfit.images.length) {
      return null;
    }

    const items = [
      {
        icon: 'download-custom-fg',
        text: __t('realm_outfit_details_edit.context_menu.download_all'),
        onClick: () => this.handleDownloadAllOutfitImages()
      }
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        contextMenuProps={{
          items,
          closeOnScroll: true
        }}
      />
    );
  };

  private renderUnpublishActions = () => {
    const { isUnpublishing } = this.props;

    return (
      <ActionButton
        color="secondary"
        disabledWithSpinner={isUnpublishing}
        onClick={this.handleUnpublish}
      >
        {__t('realm_outfit_details.button_unpublish')}
      </ActionButton>
    );
  };

  renderVideo(video: Video): JSX.Element {
    const { isBrand, hasProcessingCompleted, onRetriggerVideoProcessing, dispatchDownloadFileIndividually } = this.props;

    return (
      <RealmOutfitVideo
        hasProcessingCompleted={hasProcessingCompleted}
        onRetriggerVideoProcessing={onRetriggerVideoProcessing}
        video={video}
        isInModal={isBrand}
        onDownloadVideo={dispatchDownloadFileIndividually}
      />
    );
  }

  renderDeclineHistory(): JSX.Element {
    const { outfit, isBrand } = this.props;

    const containerClasses = classnames({
      'realm-outfit-details__section': true,
      'realm-outfit-details__declined-reason': isBrand
    });

    return (
      <div className={containerClasses}>
        {
          this.renderSection(
            __t('realm_outfit_details.sections.declined_reason'),
            <OutfitDeclineHistory declineHistories={outfit.declineHistories} />
          )
        }
      </div>
    );
  }

  render() {
    const { outfit, campaign, isBrand, deliverableId, realmOutfitAudioTypeEnabled } = this.props;
    const isPublished = outfit.status === REALM_OUTFIT_STATUS_PUBLISHED
      || outfit.status === REALM_OUTFIT_STATUS_APPROVED;
    const showDeclineHistory = !isPublished && Boolean(outfit?.declineHistories?.length);
    const showPublishedError = !isPublished && Boolean(outfit.publishedError);
    const audioType = outfit.video?.audioType ?? null;
    const tags = splitTagsByType(outfit.tags);
    const images = sortBy(outfit.images, 'sortOrder');
    const video = outfit.video;
    const isReviewModal = !isNil(deliverableId);
    const isCcm = campaign?.campaignType === 'ccm';
    const publishedCountries = isCcm
      ? campaign?.countries ?? []
      : outfit.publishedCountries ?? [];

    return (
      <section className="realm-outfit-details">
        <div className="realm-outfit-details__content">

          <div>
            {this.renderSection(
              <React.Fragment>
                {__t('realm_outfit_details.sections.image')}
                {this.renderContextMenu()}
              </React.Fragment>,
              (
                <RealmOutfitsImages
                  images={images}
                  isInModal={isBrand}
                />
              )
            )}
            {video !== null && this.renderSection(
              __t('realm_outfit_details.sections.video'),
              this.renderVideo(video)
            )}
            {video !== null && realmOutfitAudioTypeEnabled && this.renderSection(
              __t('realm_outfit_details.sections.audio'),
              this.renderAudioType(audioType)
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.tags'),
              this.renderRealmTags(tags.regular)
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.genders'),
              this.renderSelect('genders', outfit.genders)
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.gtl_tags'),
              this.renderRealmTags(tags.gtl)
            )}
            {!isBrand && this.renderSection(
              __t('realm_outfit_details.sections.reviewed_at'),
              this.renderReviewDate(outfit.reviewedAt)
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.published_countries'),
              this.renderTags(publishedCountries.map(tag => tag.toUpperCase()).sort())
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.published_dates'),
              this.renderDatesInput(outfit.publishedFrom, outfit.publishedUntil)
            )}
            {this.renderSection(
              __t('realm_outfit_details.sections.items'),
              this.renderItems(outfit.items)
            )}
            {showDeclineHistory && this.renderDeclineHistory()}
            {showPublishedError && !isBrand && this.renderSection(
              __t('realm_outfit_details.sections.published_error'),
              this.renderPublishedError()
            )
            }
            {isReviewModal && outfit.status === REALM_OUTFIT_STATUS_PENDING && !isBrand && this.renderSection(
              __t('realm_outfit_details.sections.action'),
              <RealmOutfitReview
                outfit={outfit}
              />
            )}
            {isReviewModal && isPublished && !isBrand && this.renderSection(
              __t('realm_outfit_details.sections.unpublish_action'),
              this.renderUnpublishActions()
            )}
          </div>
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ campaignBrief, realmOutfits, runtimeConfig }): StateProps => ({
  campaign: campaignBrief.brief,
  isUnpublishing: realmOutfits.isUnpublishing,
  realmOutfitAudioTypeEnabled: runtimeConfig.config.realmOutfitAudioTypeEnabled ?? false
});

const mapDispatchToProps = (dispatch, { isBrand, deliverableId }: OwnProps): DispatchProps => ({
  dispatchDownloadImages: (outfitId) => dispatch(downloadImages(outfitId, isBrand ? deliverableId : null)),
  dispatchUnpublishOutfit: (outfitId) =>
    dispatch(unPublishRealmOutfit(outfitId)),

  dispatchShowConfirmModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal()),
  dispatchDownloadFileIndividually: url => dispatch(downloadFileIndividually(url))
});

export default connect(mapStateToProps, mapDispatchToProps)(RealmOutfitDetails);
